import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { themes, scrollTop, optionalAvathar } from "services/constants";
import moment from "moment";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomAutoComplete } from "components/CustomComponents";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import {
  GlobalFieldLabel,
  globalTextFieldSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import DateRangePicker from "react-bootstrap-daterangepicker";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  approveStickyFooterBar,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

export const Daily = ({
  loading,
  dailyShiftList,
  updateDailyShift,
  selectCurrentDate,
  shiftGrpError,
  setGrpNameError,
  openDialog,
  setOpenDialog,
  filterChanges,
  setFilterChanges,
  departmentIdValue,
  setFetchWeekly,
  departmentNames,
  masterDataForSearch,
}) => {
  const rosterTableHeaderCellSx = {
    ...designShell.tableHeadCellBase,
    fontWeight: 700,
    backgroundColor: designColors.slate100,
    position: "sticky",
    top: APP_BAR_OFFSET,
    zIndex: 2,
  };
  const panelLabelSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
  };
  const panelContentSx = {
    px: 3,
    py: 2.5,
  };

  const [page, setLeavePage] = useState(0);
  const [rowsPerPage, setLeaveRowsPerPage] = useState(25);
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // Same value for every shift cell in the table (doesn't depend on the
  // row/day being rendered) — hoisted once instead of recomputed per cell.
  // Clicking a cell without this used to always show the edit dialog and
  // only check permission on click, falling back to an "Access denied: ..."
  // toast. Cells don't have a native disabled state the way buttons do, so
  // this drives both the cursor style and a hover tooltip instead.
  const canEditShift = checkEditorPermission(
    userScreenAccess?.module_details,
    "Rosters",
    "Roster",
  );
  const [userShiftsDet, setUserShiftsDet] = useState([]);
  const [curShiftDetail, setCurShiftDetail] = useState([]);
  const [shiftName, setShiftName] = useState("");
  const [shiftId, setShiftId] = useState("");
  const [shiftGrpData, setSftGrpData] = useState([]);
  const [triggerApi, setTriggerApi] = useState(false);
  const [selectDate, setSelectDate] = useState("");
  const containerRef = useRef(null);

  const [latestPayDate, setLatestPayDate] = useState(null);
  const [disable, setDisable] = useState(true);
  const [selectedDates, setSelectedDates] = useState([]);
  const [getPayDate, setGetPayDate] = useState(false);
  const [state, setState] = useState(() => {
    return {
      start: selectCurrentDate,
      end: selectCurrentDate,
    };
  });

  const { start, end } = state;
  const handleCallback = (start, end) => {
    setState({ start, end });
    const dates = [];
    const currentDate = moment(start);

    while (currentDate.isSameOrBefore(end, "day")) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }
    setSelectedDates(dates);
    setShiftName("");
    setDisable(false);
  };

  const label = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");

  useEffect(() => {
    if (getPayDate) {
      setSelectedDates([selectCurrentDate.format("YYYY-MM-DD")]);
      setState({
        start: selectCurrentDate,
        end: selectCurrentDate,
      });
      setGetPayDate(false);
    }
  }, [getPayDate, selectCurrentDate]);

  const handleChangeRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };

  const sortedDailyShiftList = dailyShiftList;

  useEffect(() => {
    if (filterChanges) {
      setFilterChanges(false);
      setLeavePage(0);
    }
  }, [filterChanges, setFilterChanges]);

  const hasDatesBeforeLatestPayDate = selectedDates.some((date) =>
    moment(date).isSameOrBefore(moment(latestPayDate, "DD-MM-YYYY"), "day"),
  );

  const updateIndividualShift = async () => {
    const shift_assignments = selectedDates.map((date) => ({
      shift_id: shiftId ?? curShiftDetail?.shift_id,
      date: date,
      is_week_off: shiftName === "Weekoff",
      unassigned: curShiftDetail?.shift_name === "Unassigned",
    }));
    if (shiftName !== "" && !hasDatesBeforeLatestPayDate) {
      updateDailyShift(userShiftsDet?.user_id, shift_assignments);
      setOpenDialog(false);
      setSelectedDates([selectCurrentDate.format("YYYY-MM-DD")]);
    } else if (shiftName === "") {
      setGrpNameError(true);
    } else {
      showToast("error", "Selected detes must after previous payrun date");
    }
  };

  const handleShiftGroup = (event, data) => {
    if (data) {
      setShiftName(data?.label);
      setShiftId(data?.shift_id);
      setDisable(false);
    } else {
      setShiftName("");
      setShiftId("");
    }
  };

  useEffect(() => {
    const fetchIndividualUserDetails = async () => {
      const res = await customGetAPI(`${domain}all/shift-filter/`);
      if (res.ok) {
        setSftGrpData(res?.responseDetails);
      }
      //  else {
      //   setSftGrpData([]);
      //   setShiftName("");
      // }
    };
    if (triggerApi) {
      fetchIndividualUserDetails();
      setTriggerApi(false);
    }
  }, [
    domain,
    selectCurrentDate,
    selectDate,
    triggerApi,
    userShiftsDet.user_id,
  ]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const clearValues = () => {
    setOpenDialog(false);
    setGrpNameError(false);
    setDisable(true);
    setSelectedDates([selectCurrentDate.format("YYYY-MM-DD")]);
  };

  const getShiftDotColor = (shift) => {
    const shiftName = (shift?.shift_name || "").toLowerCase();

    if (
      !shift?.shift_name ||
      shiftName === "unassigned" ||
      shiftName === "not joined"
    ) {
      return "#94A3B8";
    }
    if (shiftName === "weekoff") {
      return "#9CA3AF";
    }
    if (shiftName === "holiday") {
      return "#0EA5E9";
    }
    if (shiftName === "leave") {
      return "#F43F5E";
    }
    if (shiftName.includes("night")) {
      return "#8B46E8";
    }
    if (shiftName.includes("evening")) {
      return "#2F76E5";
    }
    if (shiftName.includes("morning")) {
      return "#16A34A";
    }

    return "#F59E0B";
  };

  const getShiftCellSx = (shift) => {
    const shiftName = (shift?.shift_name || "").toLowerCase();

    if (
      !shift?.shift_name ||
      shiftName === "unassigned" ||
      shiftName === "not joined"
    ) {
      return {
        backgroundColor: "#F8FAFC",
        color: "#475569",
      };
    }

    if (shiftName === "weekoff") {
      return {
        backgroundColor: "#F8FAFC",
        color: "#334155",
      };
    }

    // Was falling through to the generic assigned-shift default (a light
    // blue "#EEF4FF" that reads almost identically to Unassigned's own
    // "#F8FAFC" at a glance), so a holiday day looked indistinguishable from
    // an unassigned one.
    if (shiftName === "holiday") {
      return {
        backgroundColor: "#EFF8FF",
        color: "#075985",
      };
    }

    if (shiftName === "leave") {
      return {
        backgroundColor: "#FFF1F2",
        color: "#E11D48",
      };
    }

    return {
      backgroundColor: shift?.color_code || "#EEF4FF",
      color: "#14224F",
    };
  };

  const panelFooter = (
    <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
      <SecondaryButton onClick={clearValues}>Cancel</SecondaryButton>
      <PrimaryButton onClick={updateIndividualShift} disabled={disable}>
        Update
      </PrimaryButton>
    </Mui.Stack>
  );

  return (
    <React.Fragment>
      {loading ? (
        <Mui.Box>
          <Loader info="Loading..." />
        </Mui.Box>
      ) : (
        <>
          <Mui.Grid container>
            <Mui.TableContainer
              ref={containerRef}
              component={Mui.Paper}
              sx={{
                mt: 1,
                width: "100%",
                border: `1px solid ${designColors.slate200}`,
                borderRadius: 4,
                // Rows are cards below 900px, so nothing needs its own scroll.
                overflowX: "visible",
                overflowY: "visible",
                ml: 0,
                mr: 0,
                boxShadow: "none",
                backgroundColor: designColors.white,
              }}
            >
              <ApproveTable stickyHeader sx={{ width: "100%", minWidth: 640 }}>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow>
                    <Mui.TableCell
                      sx={{
                        ...rosterTableHeaderCellSx,
                        width: "20%",
                      }}
                      align="left"
                    >
                      Name
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        ...rosterTableHeaderCellSx,
                      }}
                      align="center"
                    >
                      Shift & Time
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {sortedDailyShiftList?.length === 0 ? (
                    <TableEmptyState dataLength={masterDataForSearch} />
                  ) : (
                    sortedDailyShiftList
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      ?.map((shiftList, index) => (
                        <React.Fragment
                          key={
                            shiftList?.user_id ||
                            `${shiftList?.user_name}-${index}`
                          }
                        >
                          {shiftList?.shifts.map((shift, i) => (
                            <Mui.TableRow
                              key={`${shiftList?.user_id || shiftList?.user_name || index}-${shift?.date || i}`}
                              sx={{
                                cursor: "pointer",
                                "& td": {
                                  borderBottom: `1px solid ${designColors.slate200}`,
                                },
                              }}
                            >
                              <Mui.TableCell
                                data-label="Name"
                                sx={{ backgroundColor: designColors.white }}
                              >
                                <Mui.Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Mui.Avatar
                                    sx={{ width: 30, height: 30 }}
                                    alt={shiftList?.user_name
                                      ?.charAt(0)
                                      .toUpperCase()}
                                    src={
                                      shiftList?.image_url ?? optionalAvathar
                                    }
                                  />
                                  <Mui.Typography
                                    sx={{
                                      fontWeight: 600,
                                      color: "#14224F",
                                    }}
                                  >
                                    {shiftList.user_name}
                                  </Mui.Typography>
                                </Mui.Stack>
                              </Mui.TableCell>

                              <Mui.Tooltip
                                title={
                                  canEditShift
                                    ? ""
                                    : "Access denied: You are not authorized to change shift"
                                }
                                arrow
                                slotProps={tooltipStyleProps}
                              >
                                <Mui.TableCell
                                  data-label="Shift & Time"
                                  sx={{
                                    p: 0.75,
                                    backgroundColor: designColors.white,
                                    cursor: canEditShift
                                      ? "pointer"
                                      : "default",
                                  }}
                                  onClick={() => {
                                    if (!canEditShift) return;
                                    setUserShiftsDet(shiftList);
                                    setCurShiftDetail(shift);
                                    setShiftName(
                                      shift?.shift_name === "Unassigned"
                                        ? ""
                                        : shift?.shift_name,
                                    );
                                    setShiftId(shift?.shift_id);
                                    setSelectDate(selectCurrentDate);
                                    setLatestPayDate(
                                      shiftList?.latest_pay_date,
                                    );
                                    setGetPayDate(true);
                                    setOpenDialog(true);
                                    setTriggerApi(true);
                                  }}
                                >
                                  <Mui.Box
                                    sx={{
                                      ...getShiftCellSx(shift),
                                      minHeight: 62,
                                      borderRadius: 2,
                                      px: 1,
                                      py: 0.9,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Mui.Stack
                                      direction="column"
                                      width={"100%"}
                                      sx={{
                                        alignItems: "center",
                                      }}
                                    >
                                      <Mui.Stack
                                        direction="row"
                                        spacing={0.6}
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                          padding: "4px 8px",
                                          borderRadius: "4px",
                                          display: "inline-flex",
                                        }}
                                      >
                                        <Mui.Box
                                          sx={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: "50%",
                                            backgroundColor:
                                              getShiftDotColor(shift),
                                            flexShrink: 0,
                                          }}
                                        />
                                        <Mui.Typography
                                          sx={{
                                            fontSize: 12,
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {shift?.shift_name
                                            ? shift?.shift_name
                                            : "Not Joined"}
                                        </Mui.Typography>
                                      </Mui.Stack>
                                      {shift?.shift_name === null ||
                                      shift?.shift_name === "Weekoff" ? (
                                        ""
                                      ) : shift?.shift_start_time &&
                                        shift?.shift_end_time ? (
                                        <>
                                          <Mui.Typography sx={{ fontSize: 10 }}>
                                            {moment(
                                              shift?.shift_start_time,
                                              "HH:mm:SS",
                                            ).format("h:mm a")}
                                            {" to "}{" "}
                                            {moment(
                                              shift?.shift_end_time,
                                              "HH:mm:SS",
                                            ).format("h:mm a")}
                                          </Mui.Typography>
                                        </>
                                      ) : null}
                                    </Mui.Stack>
                                  </Mui.Box>
                                </Mui.TableCell>
                              </Mui.Tooltip>
                            </Mui.TableRow>
                          ))}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
          </Mui.Grid>
          {sortedDailyShiftList?.length > 25 && (
            <Mui.Box sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: sortedDailyShiftList.length,
                itemLabel: "employees",
                onPageChange: setLeavePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          )}
        </>
      )}

      {openDialog ? (
        <GlobalSidePanel
          onClose={clearValues}
          open={openDialog}
          title="Edit Shift Details"
          subtitle="Review and update the selected employee shift assignment."
          bodySx={panelContentSx}
          footer={panelFooter}
        >
          <Mui.Stack spacing={2.75}>
            <RequestDetailHeader
              userDetails={{
                image_url: userShiftsDet?.image_url,
                employee_id: userShiftsDet?.employee_id,
                user_name: userShiftsDet?.user_name,
                department_name:
                  userShiftsDet?.department_name || departmentNames,
                designation: userShiftsDet?.designation,
              }}
            />
            <Mui.Box>
              <GlobalFieldLabel helperText="Choose a single date to update the shift for that specific day.">
                <Mui.Box component="span" sx={panelLabelSx}>
                  Date
                </Mui.Box>
              </GlobalFieldLabel>
              <DateRangePicker
                initialSettings={{
                  startDate: start.toDate(),
                  endDate: end.toDate(),
                  applyClass: "custom-apply-btn",
                  cancelClass: "custom-cancel-btn",
                  minDate: latestPayDate
                    ? moment(latestPayDate, "DD-MM-YYYY").add(1, "day").toDate()
                    : false,
                  locale: {
                    format: "DD/MM/YYYY",
                  },
                }}
                onCallback={handleCallback}
              >
                <Mui.TextField
                  id="reportrange"
                  variant="outlined"
                  label=""
                  placeholder="Select a date range"
                  value={label}
                  size="small"
                  InputProps={{
                    readOnly: true,
                    notched: false,
                    endAdornment: (
                      <Mui.InputAdornment position="end">
                        <Mui.IconButton>
                          <DateRangeIcon />
                        </Mui.IconButton>
                      </Mui.InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true, required: true }}
                  fullWidth
                  sx={{
                    ...globalTextFieldSx,
                    "& .MuiOutlinedInput-notchedOutline": {
                      legend: {
                        display: "none",
                      },
                    },
                  }}
                />
              </DateRangePicker>
            </Mui.Box>
            <Mui.Box>
              <GlobalFieldLabel helperText="Select the appropriate shift to assign for the chosen date. Shows all available shifts.">
                <Mui.Box component="span" sx={panelLabelSx}>
                  Shift Name
                </Mui.Box>
              </GlobalFieldLabel>
              <CustomAutoComplete
                dataList={shiftGrpData
                  ?.filter((obj) => obj?.shift_name !== shiftName)
                  .map((obj) => ({
                    ...obj,
                    label: obj?.shift_name,
                  }))}
                selectedValue={shiftName}
                updateDataFunction={handleShiftGroup}
                placeHolder="Select shift group name"
              />
              {shiftGrpError ? (
                <Mui.Typography
                  sx={{
                    pt: 0.5,
                    color: themes.helperTextColor,
                    fontSize: 12,
                  }}
                >
                  Shift name is required
                </Mui.Typography>
              ) : null}
            </Mui.Box>
          </Mui.Stack>
        </GlobalSidePanel>
      ) : null}
    </React.Fragment>
  );
};
